import { throwError as observableThrowError, Observable, of } from 'rxjs';
import { Injectable, Injector } from '@angular/core';
import {
    HttpEvent,
    HttpInterceptor,
    HttpHandler,
    HttpRequest,
    HttpErrorResponse
} from '@angular/common/http';
import { $log } from '../../utils/logger';
import { Subject } from 'rxjs';
import { take, map, catchError, switchMap, finalize, withLatestFrom } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { isNullOrUndefined } from '@core/utils/object';
import { AuthService } from '@core/state/auth/auth.service';

/**
 * Authentication Interceptor.
 * Appends authentication to request header and attempts token refresh as required during 401s.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    private tokenRefreshedSource = new Subject();
    private tokenRefreshed$ = this.tokenRefreshedSource.asObservable();
    private isRefreshingToken = false;
    private authService: AuthService;

    constructor(private injector: Injector) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Append authorization for endpoint and relative urls
        let requiresAuth: boolean = true;

        if (!isNullOrUndefined((req as any).requiresAuth) && (req as any).requiresAuth === false) {
            requiresAuth = false;
        } else if (req.url && req.url.indexOf('http') === 0 || req.url.indexOf('https') === 0) {
            if (req.url.indexOf(environment.api.endpointBase) !== 0) {
                requiresAuth = false;
            }
        }

        this.authService = this.injector.get(AuthService);

        // Add session authorization if no headers passed
        let authHeader: string = this.authService.getAuthorizationHeader();

        const loginUrl: string = environment.api.endpointAuth + '/login';
        const passworUrl: string = environment.api.endpointAuth + '/password';

        // NOTE: Temporary 401 bypassing for user's phone and email code verification
        const guidSegment: string = '(?:\{{0,1}(?:[0-9a-fA-F]){8}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){4}-(?:[0-9a-fA-F]){12}\}{0,1})';
        const temporaryUserPhoneCodeVerification: RegExp = new RegExp(environment.api.endpointV2 + '/users/' + guidSegment + '/phone');
        const temporaryUserEmailCodeVerification: RegExp = new RegExp(environment.api.endpointV2 + '/users/' + guidSegment + '/email');

        if (requiresAuth && !req.headers.has('Authorization') && authHeader && authHeader.length > 0
            && (req.url !== loginUrl || !!req.body?.is_changing_companies) 
            && req.url !== passworUrl) {

            // Clone request and add header
            const authReq = req.clone({headers: req.headers.set('Authorization', authHeader)});
            return next.handle(authReq)
              .pipe(
                catchError((err: any) => {

                    if (err instanceof HttpErrorResponse) {
                        if (err.status !== -1 && err.status !== 0 && err.status !== 401 && err.status !== 404) {

                            // ERROR: Unknown fail
                            $log.error('Request Failed', err);

                        } else if (err.status === 404) {
                            $log.warn('Request Not Found', err);
                        }

                        // $log.debug('Auth inteceptor', err);
                        if (err.status === 401 &&
                            // NOTE: Temporary 401 bypassing for user's phone and email code verification
                            !temporaryUserEmailCodeVerification.test(req.url) &&
                            !temporaryUserPhoneCodeVerification.test(req.url)
                        ) {
                            return this.refreshToken(!!req.body?.is_changing_companies)
                              .pipe(
                                withLatestFrom(this.authService.getUserAuthentication()),
                                switchMap(([_, userAuth]) => {

                                    // Refresh request auth header
                                    authHeader = this.authService.getAuthorizationHeader();

                                    const clonedRequestItems: any = {
                                        headers: req.headers.set('Authorization', authHeader)
                                    };

                                    if (!!req.body?.is_changing_companies && req.url === loginUrl && !!userAuth?.access_token) {
                                        // amend access token
                                        
                                        clonedRequestItems.body = {
                                            ...req.body,
                                            access_token: userAuth?.access_token
                                        };
                                    }

                                    const nextReq = req.clone(clonedRequestItems);

                                    return next.handle(nextReq);
                                }),
                                catchError((err: any) => {
                                    return observableThrowError(err);
                                })
                              )
                        } else {
                            // continue the existing stream
                            return observableThrowError(err);
                        }
                    }
                })
              );
        } else {

            // No authentication headers
            return next.handle(req);
        }
    }

    private refreshToken(isChangingCompanies: boolean = false): Observable<any> {
        if (this.isRefreshingToken) {

            // Cache request as observable
            return new Observable((observer) => {
                this.tokenRefreshed$.subscribe(() => {
                    observer.next();
                    return observer.complete();
                });
            });
        } else {

            // Refresh token and resolve
            this.isRefreshingToken = true;
            return this.authService.refreshToken2(isChangingCompanies)
              .pipe(
                map(() => {
                    this.tokenRefreshedSource.next(true);
                    return this.tokenRefreshedSource.complete();
                }),
                
                take(1),
                finalize(() => {
                    this.isRefreshingToken = false;
                })
              )
        }
    }
}
